import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MdRotateLeft } from "react-icons/md";
import { Button } from "../../core/basic/Button";
import { RT8ColumnSelector } from "../../core/blocks/MetricSelector";
import { SearchInput } from "../../fields/base/TextField";
import { createAppColumnHelper, useClientTable } from "../../table/tableFeatures";
import { usePersistedTableState } from "../../table/usePersistedTableState";
import { commaSearchFilterFn } from "../../table/filterFns";
import { resetTable } from "../../table/utils";
import { RT8Pagination } from "../../table/Pagination";
import { Table } from "../../table/Table";
import { TableErrorWrapper } from "../../table/TableErrorWrapper";
import { HStack } from "../../../../styled-system/jsx";
import { css } from "../../../../styled-system/css";
import { snakeCaseToSentenceCase } from "../../../utils/formatText";
import { SellerIdLink } from "./SellerIdLink";
import {
  type AmazonAdsClientProfiles,
  type AmazonProfile,
  profileKey,
  useAmazonAdsClients,
  useRemoteAmazonProfiles,
} from "./useAmazonProfiles";

const columnHelper = createAppColumnHelper<AmazonProfile>();

const globalFilterFn = commaSearchFilterFn<AmazonProfile>((profile) =>
  [profile.profile_name, profile.marketplaces, profile.profile_type, profile.entity_id].join(" "),
);

/**
 * Every profile Amazon returns for the current org's Ads credential, with the Ads login (client) that
 * reaches it. Fetches from Amazon when it mounts.
 */
export function RemoteAmazonProfilesTable() {
  const { t } = useTranslation(["onboarding", "common"]);
  const profilesQuery = useRemoteAmazonProfiles();
  // Only the login columns read the clients, and they are hidden by default, so rows don't wait for them.
  const clientsQuery = useAmazonAdsClients();

  const clients = clientsQuery.data;
  // The login columns look a profile's login up here rather than copying it onto every row.
  const clientByProfile = useMemo(() => {
    const byProfile = new Map<string, AmazonAdsClientProfiles>();
    for (const client of clients ?? []) {
      for (const profile of client.profiles) {
        byProfile.set(profileKey(profile), client);
      }
    }
    return byProfile;
  }, [clients]);

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: "remoteAmazonProfilesTable",
    initialState: {
      sorting: [{ id: "profile_name", desc: false }],
      columnVisibility: { amazon_user_email: false, amazon_user_name: false, amazon_user_id: false },
    },
  });

  const columns = useMemo(
    () => [
      columnHelper.accessor("profile_name", {
        header: t(($) => $.onboarding["Profile Name"]),
        size: 250,
        enableHiding: false,
        sortFn: "alphanumeric",
      }),
      columnHelper.accessor((profile) => profile.marketplaces.join(", "), {
        id: "marketplaces",
        header: t(($) => $.onboarding["Profile Marketplaces"]),
        size: 180,
        sortFn: "alphanumeric",
      }),
      columnHelper.accessor("profile_type", {
        header: t(($) => $.onboarding["Profile Type"]),
        size: 130,
        enableResizing: false,
        sortFn: "alphanumeric",
        cell: ({ getValue }) => snakeCaseToSentenceCase(getValue()),
      }),
      columnHelper.accessor((profile) => clientByProfile.get(profileKey(profile))?.amazon_user_email ?? undefined, {
        id: "amazon_user_email",
        header: t(($) => $.onboarding["Authorized By User Email"]),
        size: 250,
        sortFn: "alphanumeric",
      }),
      columnHelper.accessor((profile) => clientByProfile.get(profileKey(profile))?.amazon_user_id ?? undefined, {
        id: "amazon_user_id",
        header: t(($) => $.onboarding["Authorized By User ID"]),
        size: 260,
        sortFn: "alphanumeric",
      }),
      columnHelper.accessor((profile) => clientByProfile.get(profileKey(profile))?.amazon_user_name ?? undefined, {
        id: "amazon_user_name",
        header: t(($) => $.onboarding["Authorized By User Name"]),
        size: 200,
        sortFn: "alphanumeric",
      }),
      columnHelper.accessor((profile) => (profile.profile_type === "seller" ? profile.entity_id : undefined), {
        id: "seller_id",
        header: t(($) => $.onboarding["Seller ID"]),
        size: 180,
        enableResizing: false,
        sortFn: "alphanumeric",
        sortUndefined: "last",
        cell: ({ row, getValue }) => {
          const sellerId = getValue();
          return sellerId ? <SellerIdLink sellerId={sellerId} marketplace={row.original.marketplaces[0]} /> : null;
        },
      }),
    ],
    [t, clientByProfile],
  );

  const table = useClientTable({
    data: profilesQuery.data,
    columns,
    globalFilterFn,
    initialState,
    state,
    ...setters,
    meta: {
      showSkeleton: profilesQuery.isPending,
    },
  });

  return (
    <TableErrorWrapper error={profilesQuery.error} userMessage={t(($) => $.onboarding["Error loading profiles"])}>
      <HStack padding={"small"} fontSize={"large"}>
        <Button
          variant={"contentOnly"}
          aria-label={t(($) => $.common.table["Reset table filters"])}
          onPress={() => resetTable(table)}
          fontSize={"xxlarge"}
        >
          <MdRotateLeft />
        </Button>
        <RT8ColumnSelector table={table} />
        <SearchInput
          aria-label={t(($) => $.common.actions["Search"])}
          isExpandable
          css={{ flex: "0 1 350px" }}
          value={table.state.globalFilter ?? ""}
          onChange={table.setGlobalFilter}
        />
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table table={table} />
    </TableErrorWrapper>
  );
}
